import loadable from "@loadable/component";
import classnames from "classnames";
import { isLeft } from "fp-ts/Either";
import { inject } from "mobx-react";
import { createElement, Fragment, useEffect } from "react";
import { ShareButtons } from "../../components/integrations/ShareButtons.component";
import { blockMap } from "../../content-blocks";
import { CalendarPayloadResolver } from "@ihr-radioedit/inferno-core";
import { BlockBase, BlockBaseLegacy } from "@ihr-radioedit/inferno-core";
import { isBlockScheduled } from "../../lib/guards";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { Container } from "../../ui";
import { ContentProps } from "./Content.component";
import "./Content.style.scss";
import { ILog } from "@ihr-radioedit/inferno-core";
const log = ILog.logger("CalendarContent");

const ContentFooter = loadable(() => import("./ContentFooter.component"));
const CalendarFrontMatter = loadable(() => import("./CalendarFrontMatter.component"));

export const CalendarContent = inject("store")(({ content, block, store }: ContentProps) => {
  if (!store || content === null) {
    return null;
  }

  useEffect(() => {
    store.storeBlock(block);
  });

  const { site, request } = store;
  const resolvedPayload = CalendarPayloadResolver.decode(content.payload);
  if (isLeft(resolvedPayload)) {
    log.error(`Payload did not validate! ${getPaths(resolvedPayload)}`);
    return null;
  }
  const payload = resolvedPayload.right;
  const { fields, ingestion } = payload;

  if (!fields) {
    return null;
  }

  const url = site.getShareUrl(request);

  const containerCss = classnames("content-layout-container calendar-content", {
    sponsored: fields.is_sponsored,
  });

  return (
    <Container className={containerCss}>
      {!block.tags?.includes("display-hints/no-front-matter") ? (
        <CalendarFrontMatter content={content} block={block} url={url} />
      ) : null}

      <article className="content-detail-container">
        {payload.fields.blocks.value?.map((b, i) => (
          <Fragment key={i}>
            <CalendarContentBlock block={b} />
          </Fragment>
        ))}
      </article>
      {fields.is_sponsored ? (
        <ContentFooter type="sponsored" author={content?.summary.author} siteName={site.sections.general?.name} />
      ) : null}
      {ingestion?.feed_vendor && ingestion?.feed_permalink ? (
        <ContentFooter
          type="partner"
          author={content?.summary.author}
          siteName={ingestion?.feed_vendor}
          url={ingestion?.feed_permalink}
        />
      ) : null}
      <ShareButtons url={url} title={content.summary.title} desc={content.summary.description} setStyles={false} />
      <hr className="divider" />
    </Container>
  );
});

export const CalendarContentBlock = ({ block }: { block: BlockBase }) => {
  if (!block) {
    throw new Error("No Block given to ContentBlock");
  }

  if (!isBlockScheduled(block) || !blockMap[block.type]) {
    return null;
  }

  const el = createElement<BlockBaseLegacy>(blockMap[block.type], block);
  const blockClass = classnames("content-block", { [`type-${block.type}`]: block.type });

  return <section className={blockClass}>{el}</section>;
};

export default CalendarContent;
